"use client";

import { AlertTriangle, ExternalLink, Wrench } from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { Modal } from "@/components/ui/modal";
import { ForumRichContent } from "@/features/forum/components/forum-rich-editor";
import { isSystemMaster } from "@/lib/permissions";
import type { User } from "@/lib/types";
import {
  acknowledgeAnnouncementPopup,
  getRuntimeState,
  listPendingAnnouncementPopups,
  type RuntimeState,
  type SystemAnnouncement,
} from "./system-api";
import { useNexusFreshEntry } from "./use-nexus-presence";

const RETURN_TO_KEY = "innova-maintenance-return-to";

export function SystemRuntimeClient({ user }: { user: User }) {
  const pathname = usePathname();
  const router = useRouter();
  const freshEntry = useNexusFreshEntry();
  const bypass = isSystemMaster(user.permissionGrants);
  const [runtime, setRuntime] = useState<RuntimeState | null>(null);
  const [popups, setPopups] = useState<SystemAnnouncement[]>([]);
  const [acknowledging, setAcknowledging] = useState(false);

  const refreshRuntime = useCallback(async () => {
    try {
      const state = await getRuntimeState();
      setRuntime(state);
      const isHome = pathname === "/app" || pathname === "/app/";
      if (
        state.maintenanceActive &&
        !bypass &&
        !isHome &&
        pathname !== "/maintenance"
      ) {
        window.sessionStorage.setItem(RETURN_TO_KEY, pathname || "/app");
        router.replace("/maintenance");
      }
    } catch {
      // The next lightweight poll recovers transient availability failures.
    }
  }, [bypass, pathname, router]);

  useEffect(() => {
    const initialTimer = window.setTimeout(() => void refreshRuntime(), 0);
    const interval = window.setInterval(() => void refreshRuntime(), 30_000);
    return () => {
      window.clearTimeout(initialTimer);
      window.clearInterval(interval);
    };
  }, [refreshRuntime]);

  useEffect(() => {
    function handleMaintenanceError(event: Event) {
      const detail = (event as CustomEvent<{ code?: string }>).detail;
      if (detail?.code === "SYSTEM_MAINTENANCE" && !bypass) {
        window.sessionStorage.setItem(RETURN_TO_KEY, pathname || "/app");
        router.replace("/maintenance");
      }
    }
    window.addEventListener("innova:api-error", handleMaintenanceError);
    return () =>
      window.removeEventListener("innova:api-error", handleMaintenanceError);
  }, [bypass, pathname, router]);

  useEffect(() => {
    if (freshEntry !== true) return;
    let disposed = false;
    void listPendingAnnouncementPopups()
      .then((items) => {
        if (!disposed) setPopups(items);
      })
      .catch(() => undefined);
    return () => {
      disposed = true;
    };
  }, [freshEntry]);

  const currentPopup = popups[0];
  async function acknowledgeCurrentPopup() {
    if (!currentPopup || acknowledging) return;
    setAcknowledging(true);
    try {
      await acknowledgeAnnouncementPopup(currentPopup.id);
      setPopups((current) => current.slice(1));
    } finally {
      setAcknowledging(false);
    }
  }

  return (
    <>
      {runtime?.maintenanceActive && bypass ? (
        <div className="flex items-center justify-between gap-3 border-b border-amber-200 bg-amber-50 px-5 py-2 text-sm text-amber-950 dark:border-amber-900/70 dark:bg-amber-950/40 dark:text-amber-100">
          <span className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> Manutenção do sistema em
            andamento
          </span>
          <span className="text-xs">
            Previsão: {formatDateTime(runtime.endsAt)}
          </span>
        </div>
      ) : null}
      <Modal
        description={
          currentPopup
            ? `Publicado em ${formatDateTime(currentPopup.publishAt)}`
            : undefined
        }
        open={Boolean(currentPopup)}
        onClose={acknowledgeCurrentPopup}
        size="lg"
        height="auto"
        title={currentPopup?.title ?? "Comunicado"}
        footer={
          <div className="flex justify-end">
            <button
              className="rounded-md bg-slate-950 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 dark:bg-white dark:text-slate-950"
              disabled={acknowledging}
              onClick={acknowledgeCurrentPopup}
              type="button"
            >
              {acknowledging ? "Confirmando..." : "Estou ciente"}
            </button>
          </div>
        }
      >
        {currentPopup ? (
          <div className="space-y-4 text-sm leading-6 text-slate-700 dark:text-slate-300">
            <div className="flex items-center gap-2 rounded-md bg-sky-50 px-3 py-2 font-medium text-slate-950 dark:bg-sky-950/40 dark:text-white">
              <Wrench className="h-4 w-4 text-sky-600" />
              {severityLabel(currentPopup.severity)}
            </div>
            <ForumRichContent
              className="max-w-none [&_img]:h-auto [&_img]:max-w-full"
              content={currentPopup.body}
              contentFormat={
                looksLikeRichHtml(currentPopup.body) ? "html" : "plain"
              }
            />
            {currentPopup.expiresAt ? (
              <p className="text-xs text-slate-500">
                Válido até {formatDateTime(currentPopup.expiresAt)}
              </p>
            ) : null}
            {currentPopup.deepLink ? (
              <Link
                className="inline-flex items-center gap-1 font-medium text-sky-700 hover:underline dark:text-sky-300"
                href={currentPopup.deepLink}
              >
                <ExternalLink className="h-4 w-4" /> Abrir contexto
              </Link>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </>
  );
}

function looksLikeRichHtml(value: string) {
  return /<\/?[a-z][\s\S]*>/i.test(value);
}

export function getMaintenanceReturnTo() {
  if (typeof window === "undefined") return "/app";
  const target = window.sessionStorage.getItem(RETURN_TO_KEY);
  window.sessionStorage.removeItem(RETURN_TO_KEY);
  return target?.startsWith("/") ? target : "/app";
}

export function formatDateTime(value: string | null) {
  if (!value) return "não informada";
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
    timeZone: "America/Sao_Paulo",
  }).format(new Date(value));
}

function severityLabel(severity: SystemAnnouncement["severity"]) {
  return {
    CRITICAL: "Comunicado crítico",
    INFO: "Comunicado",
    MAINTENANCE: "Manutenção",
    WARNING: "Atenção",
  }[severity];
}
